// ============================================================================
// DIE TERMINSTEUERUNG IST EIN KALENDER.
// ============================================================================
// ORIGINALTEXT MARVIN (03.09.2026, mit zwei Bildschirmfotos des Terminfensters)
//   „die terminsteuerung ist immernoch nicht so wie ich sie dir in den bildern
//    gezeigt habe und wie ich sie brauche. behebe den fehler final!"
//
// Davor, am selben Tag:
//   „wir brauchen bei uns diese terminsteuerung wieder. das hatte alles was wir
//    brauchen ... auch mehrere tage bei größerem tiefbauaufwand konnte
//    eingeplant werden."
//
// **DREIMAL DASSELBE, UND ZWEIMAL HABE ICH EIN FORMULAR NACHGEBESSERT.** Erst
// kam ein Datumsfeld dazu, dann ein Zahlenfeld „Tage am Stück" - und beide
// beantworten die Frage nicht, mit der jemand dieses Fenster aufmacht: **wann
// ist überhaupt Platz.** Ein Datumsfeld nimmt jeden Tag an. Es nimmt den
// Sonntag an, den Feiertag, den Tag, an dem die Kolonne schon woanders gräbt,
// und den Tag vor dem Ende des Gemeindevorlaufs. Was danebensteht, ist ein
// Riegel, der erst NACH der Eingabe etwas sagt - **und wer dreimal abgewiesen
// wird, sucht sich den Tag am Ende woanders.**
//
// DIE VORGÄNGERLÖSUNG HATTE DAFÜR EIN MONATSGITTER, und darin lag die ganze
// Auskunft: welcher Tag frei ist, an welchem die Firma Urlaub gemeldet hat, wo
// schon etwas steht und wie viel. Rechts daneben die halben Stunden des Tages,
// darunter die Leiste der gewählten Tage. Das ist hier nachgebaut, mit den
// Daten dieses Hauses.
//
// DREI DINGE STEHEN AUSDRÜCKLICH NICHT HIER:
//
//   Feiertage   Welcher Tag keiner ist, sagt der SERVER (`p.feiertage`) - es
//               hängt am Bundesland der Auftrags-PLZ. Hier wird die Liste
//               angewendet und nichts gerechnet.
//   Belegung    Sie kommt aus `/api/termine`, derselben Abfrage, die auch der
//               Wochenplan liest. Zwei Abfragen über dieselbe Frage laufen
//               auseinander.
//   Der Riegel  Was gegen eine Buchung spricht, entscheidet der Server. Der
//               Kalender macht den WEG kurz, nicht die ENTSCHEIDUNG.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';

import { api, datum, kalenderwoche, heuteIso} from '../api.ts';
import {
  belegteFenster, dauerText, dauerTage, fuehrendeFelder, luecken,
  monatVerschieben, naechsterWerktag, slotliste, spanne, tageImMonat,
  tageslage, werktagskette, zeitKlick, zuMin,
} from './terminraster.ts';
import { fragen } from './rueckfrage.tsx';

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli',
                'August', 'September', 'Oktober', 'November', 'Dezember'];
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export interface Bautag { iso: string; von: string; bis: string; }

/**
 * Die Abkürzungen aus dem Bestand: „1 Tag / 1,5 Tage / 2 Tage / 3 Tage".
 *
 * Sie sind der Regelfall und ersparen drei Klicks. Der halbe Tag ist dabei
 * KEINE Zier: ein Hausanschluss über anderthalb Tage ist die häufigste
 * mehrtägige Baustelle, und ein zweiter GANZER Tag sperrt der Firma einen
 * Nachmittag, an dem sie fahren könnte.
 */
const ABKUERZUNGEN = [1, 1.5, 2, 3];

interface Props {
  /** Die Antwort von /terminpruefung: Feiertage, Fenster, Vorlauf, heute. */
  p: any;
  /** Der angezeigte Monat, „JJJJ-MM". */
  monat: string;
  setMonat: (m: string) => void;
  /** Die gewählte Firma. Ohne sie weiss der Kalender nichts über Belegung. */
  firma: string;
  /** Die gewählten Tage mit ihren Zeiten. Sie sind das, was gebucht wird. */
  tage: Bautag[];
  setTage: (t: Bautag[]) => void;
  /** Darf dieser Termin mehrere Tage haben? Nur der Tiefbau darf. */
  mehrtag: boolean;
  /** .507: der vorbelegte alte Termin. Steht nur er, ERSETZT ein Klick auf einen
   *  anderen Tag ihn, statt ihn als zweiten Bautag anzuhängen (Krüsmannstr. 22). */
  vorbelegt?: string | null;
  /**
   * WELCHE GEWERKE NICHT ALS BELEGUNG ZÄHLEN - der eigene beim Umbuchen.
   *
   * Der Server rechnet das seit jeher so (`ausser = eigenes.id` in
   * `POST /auftraege/:id/termin`), der Kalender nicht: beim Umbuchen war die
   * Zeit, auf der der Termin gerade steht, ausgegraut. **Ein Slot, den nur
   * der eigene Termin belegt, ist beim Umbuchen frei** - alles andere ist
   * eine Belegung, die sich selbst im Weg steht.
   */
  ausser?: string[];
}

export default function Terminkalender(
  { p, monat, setMonat, firma, tage, setTage, mehrtag, ausser, vorbelegt }: Props
) {
  const [daten, setDaten] = useState<any>(null);
  const [laedt, setLaedt] = useState(false);
  /* DER TAG, DESSEN ZEITEN RECHTS STEHEN. Er ist NICHT dasselbe wie die
     Auswahl: bei drei Bautagen sind drei Tage gewählt und einer aufgeschlagen.
     Ohne diese Trennung liesse sich am zweiten Tag keine andere Zeit setzen. */
  const [aktiv, setAktiv] = useState('');
  /* HIER STAND EIN ANKER, UND ER WAR DER GRUND FÜR DIE BEANSTANDUNG.
     Der erste Klick setzte ihn, der zweite spannte auf, der dritte fing von
     vorn an - und eine gewählte Zeit liess sich gar nicht abwählen, ausser
     indem man woanders hinklickte und alles verlor. Was ein Klick bedeutet,
     steht jetzt in `zeitKlick` (`teile/terminraster.ts`), ohne zweiten
     Zustand daneben. */

  /* WELCHE FENSTER EIN MENSCH AN DIESEM TAG WIRKLICH ANGEKLICKT HAT.
     ========================================================================
     ORIGINALTEXT MARVIN (07.09.2026)
       „bei der Terminvergabe kann ich nicht einfach zeiten frei selektieren,
        er markiert immer die uhrzeiten davor auch."

     EIN TERMIN HAT NUR `von` UND `bis` - also eine SPANNE, und eine Spanne
     hat keine einzelnen Fenster. Solange die Auswahl allein daraus gerechnet
     wurde, KONNTE ein Klick nichts anderes tun, als den Rand zu verschieben:
     das ist der Bau hinter der Beanstandung und nicht ein Versehen an einer
     Zeile.

     Die freie Auswahl steht deshalb HIER, je Tag, und die Buchung bleibt die
     Spanne von der ersten bis zur letzten gewählten halben Stunde. Ein Tag,
     der hier NICHT steht, trägt seine Zeit noch so, wie ein Blockknopf sie
     gesetzt hat - und genau das entscheidet, ob der nächste Klick die Zeit
     NEU setzt oder eine dazunimmt. Ein Vorschlag, den man einzeln abklicken
     müsste, wäre bei „Ganzer Tag" fünfzehn Klicks. */
  const [auswahl, setAuswahl] = useState<Record<string, string[]>>({});

  const fenster = p?.fenster;
  const ganz = fenster?.ganz;
  const alleSlots = useMemo(
    () => (ganz ? slotliste(ganz.von, ganz.bis) : []), [ganz?.von, ganz?.bis]);

  /* DIE FEIERTAGE ALS MENGE, ZUM NACHSCHLAGEN. Sie kommen vom Server und
     decken den Monat und seine beiden Nachbarn ab - der nächste Werktag nach
     dem 31. liegt im Folgemonat. */
  const feiertage = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of p?.feiertage ?? []) m.set(f.iso, f.name);
    return m;
  }, [p?.feiertage]);
  const freieTage = useMemo(() => new Set(feiertage.keys()), [feiertage]);
  /* WIE WEIT DIE LISTE REICHT. Darüber hinaus wird NICHTS geraten: eine Kette,
     die über den Rand der Feiertagsliste läuft, könnte an einem Feiertag
     enden, ohne es zu wissen. */
  const bisWohin = useMemo(() => {
    const naechster = monatVerschieben(monat, 1);
    const t = tageImMonat(naechster);
    return t.length ? t[t.length - 1] : '';
  }, [monat]);

  const tageImRaster = useMemo(() => tageImMonat(monat), [monat]);
  const heute = p?.heute ?? heuteIso();

  // ---------------------------------------------------------- Die Belegung
  /* SIE KOMMT AUS DERSELBEN ABFRAGE WIE DER WOCHENPLAN. Termine und Urlaub
     dieser Firma, für den ganzen Monat auf einmal. */
  useEffect(() => {
    if (!firma || !tageImRaster.length) { setDaten(null); return; }
    let weg = false;
    setLaedt(true);
    const q = new URLSearchParams({
      von: tageImRaster[0], bis: tageImRaster[tageImRaster.length - 1], firma,
    });
    api.termine(q)
      .then((d) => { if (!weg) setDaten(d); })
      .catch(() => { if (!weg) setDaten(null); })
      .finally(() => { if (!weg) setLaedt(false); });
    return () => { weg = true; };
  }, [firma, monat, tageImRaster.length]);

  /** Was an diesem Tag schon steht: die Termine dieser Firma. */
  const jeTag = useMemo(() => {
    const m = new Map<string, { belegt: Set<string>; was: string[] }>();
    const dazu = (iso: string, von: any, bis: any, text: string) => {
      if (!iso) return;
      const e = m.get(iso) ?? { belegt: new Set<string>(), was: [] };
      for (const s of belegteFenster([{ von, bis }])) e.belegt.add(s);
      e.was.push(text);
      m.set(iso, e);
    };
    /* HIER STAND EINE ZWEITE SCHLEIFE ÜBER `daten.blockungen`. Die
       Terminsperre gibt es seit dem 18.09.2026 nicht mehr: ein Fenster ist
       gebucht oder frei. */
    const eigen = new Set(ausser ?? []);
    for (const t of daten?.termine ?? []) {
      if (eigen.has(t.id)) continue;
      dazu(t.termin_datum, t.termin_von, t.termin_bis,
           `${t.strasse ?? ''} ${t.hausnummer ?? ''}`.trim() || 'Termin');
    }
    return m;
  }, [daten, (ausser ?? []).join(',')]);

  const meldungJeTag = useMemo(() => {
    const m = new Map<string, any>();
    for (const v of daten?.verfuegbar ?? []) m.set(String(v.datum).slice(0, 10), v);
    return m;
  }, [daten]);

  const lageVon = (iso: string) => tageslage({
    iso, heute,
    feiertag: feiertage.get(iso),
    meldung: meldungJeTag.get(iso) ?? null,
    belegt: jeTag.get(iso)?.belegt.size ?? 0,
    fenster: alleSlots.length,
  });

  // ------------------------------------------------------- Tage auswählen

  const gewaehlt = new Map(tage.map((t) => [t.iso, t]));
  const letzter = tage.length ? tage[tage.length - 1].iso : '';
  const kommtDazu = mehrtag && letzter
    ? naechsterWerktag(letzter, freieTage, bisWohin) : '';

  /**
   * EIN KLICK AUF EINEN TAG WÄHLT IHN - UND EIN ZWEITER WÄHLT IHN AB.
   *
   * ORIGINALTEXT MARVIN (03.09.2026)
   *   „bei der terminsteuerung bei dem tag und uhrzeit auch
   *    selektieren/delseketieren mehrere tage/zeiten soll möglich sein,
   *    anstatt wegzuklicken in ein anderen bereich"
   *
   * Bis .92 machte ein Klick auf einen schon gewählten Tag ihn nur
   * AUFGESCHLAGEN; abwählen liess er sich nur über das × am Chip, und den
   * gibt es nur beim mehrtägigen Tiefbau. Wer einen Tag loswerden wollte,
   * klickte deshalb irgendwo anders hin - und verlor damit die ganze
   * Auswahl.
   *
   * UND MEHRERE TAGE STEHEN NEBENEINANDER, nicht nur der unmittelbar
   * folgende Werktag. Eine Kolonne, die Montag und Mittwoch gräbt und am
   * Dienstag woanders ist, gibt es; bis .92 hat ein Klick auf den Mittwoch
   * den Montag weggeworfen. Was zwischen zwei gewählten Tagen liegt, steht
   * unten als „Übersprungen" - nichts verschwindet still.
   *
   * BEI EINEM EINTÄGIGEN GEWERK BLEIBT ES BEI EINEM TAG. Zwei Tage wären
   * dort zwei Termine, und die bucht man einzeln.
   */
  async function tagAnklicken(iso: string) {
    const lage = lageVon(iso);
    if (!lage.waehlbar) return;
    if (gewaehlt.has(iso)) { tagEntfernen(iso); return; }
    /* .560: NICHT FREIGEGEBEN? Erst fragen, dann wählen. */
    if (lage.rueckfrage && !await fragen(`${datum(iso)}: ${lage.rueckfrage}\n\nFährt die Firma an diesem Tag trotzdem?`, { titel: 'Kein freigegebener Tag', ja: 'Ja, Tag wählen', nein: 'Nein' })) return;
    /* .507: DER ALTE TERMIN IST KEIN ERSTER BAUTAG. Marvin, Krüsmannstr. 22:
       „umgebucht vom 12.11. auf den 16.11. ... Im Kunden ist es aber immer
       noch der 12. In der Wochenplanung ist es als zweiter Tag hinterlegt."
       Verlängern geht weiter über „1,5 Tage", „2 Tage" oder einen weiteren
       Klick, sobald der neue Tag steht. */
    if (mehrtag && vorbelegt && tage.length === 1 && tage[0].iso === vorbelegt) {
      setTage([{ iso, von: ganz.von, bis: ganz.bis }]);
      setAktiv(iso);
      return;
    }
    if (mehrtag && tage.length) {
      const neu = [...tage, { iso, von: ganz.von, bis: ganz.bis }]
        .sort((a, b) => (a.iso < b.iso ? -1 : 1));
      setTage(neu);
      setAktiv(iso);
      return;
    }
    setTage([{ iso, von: ganz.von, bis: ganz.bis }]);
    setAktiv(iso);
  }

  function tagEntfernen(iso: string) {
    const rest = tage.filter((t) => t.iso !== iso);
    setTage(rest);
    if (aktiv === iso) setAktiv(rest.length ? rest[rest.length - 1].iso : '');
  }

  function abkuerzen(n: number) {
    const start = tage.length ? tage[0].iso : aktiv;
    if (!start || !ganz) return;
    const ganzeTage = Math.floor(n);
    const halber = n - ganzeTage >= 0.5;
    const kette = werktagskette(start, ganzeTage + (halber ? 1 : 0),
                                freieTage, bisWohin);
    const neu: Bautag[] = kette.map((iso, i) => ({
      iso,
      von: ganz.von,
      /* DER HALBE TAG ENDET, WO DER VORMITTAG ENDET. Die Zeit kommt aus dem
         Fenster des Servers und nicht aus einer zweiten Liste hier. */
      bis: halber && i === kette.length - 1 && kette.length > 1
        ? (fenster?.vormittag?.bis ?? ganz.bis) : ganz.bis,
    }));
    setTage(neu);
    setAktiv(neu.length ? neu[0].iso : '');
  }

  // ------------------------------------------------------ Zeiten am Tag

  const aktiverTag = tage.find((t) => t.iso === aktiv)
    ?? (tage.length ? tage[tage.length - 1] : null);
  const belegtHeute = aktiverTag
    ? (jeTag.get(aktiverTag.iso)?.belegt ?? new Set<string>()) : new Set<string>();
  /* WAS AN DIESEM TAG GEWÄHLT IST: was er angeklickt hat, sonst der Block. */
  const vonHand = !!aktiverTag && Array.isArray(auswahl[aktiverTag.iso]);
  const gewaehlteSlots = useMemo(() => {
    if (!aktiverTag) return new Set<string>();
    const eigen = auswahl[aktiverTag.iso];
    return new Set(eigen ?? slotliste(aktiverTag.von, aktiverTag.bis));
  }, [aktiverTag?.iso, aktiverTag?.von, aktiverTag?.bis,
      aktiverTag ? auswahl[aktiverTag.iso] : null]);

  /* DIE FENSTER, DIE MITGEBUCHT WERDEN, OHNE GEWÄHLT ZU SEIN.
     Wer 13:00 und 15:00 anklickt, bucht 13:00 bis 15:30 - **also zwei
     Stunden, die er nicht angeklickt hat.** Sie werden nicht heimlich
     geschlossen und nicht verschwiegen: sie stehen in der Leiste mit einer
     eigenen Farbe, und darunter steht der Satz dazu. */
  const luecke = useMemo(
    () => luecken(alleSlots, [...gewaehlteSlots]),
    [alleSlots, gewaehlteSlots]);
  const luecke2 = new Set(luecke);
  const luekeBelegt = luecke.some((s) => belegtHeute.has(s));

  function zeitSetzen(iso: string, von: string, bis: string) {
    setTage(tage.map((t) => (t.iso === iso ? { ...t, von, bis } : t)));
  }

  /** Die freie Auswahl eines Tages setzen und die Spanne daraus buchen. */
  function auswahlSetzen(iso: string, slots: string[]) {
    setAuswahl({ ...auswahl, [iso]: slots });
    const s = spanne(slots);
    if (s) zeitSetzen(iso, s.von, s.bis);
  }

  /* WAS EIN KLICK AUF EINE ZEIT BEDEUTET, STEHT IN `zeitKlick` und nicht
     hier: das eigene Fenster dazu oder weg, und solange die Zeit noch aus
     einem Blockknopf kommt, setzt der Klick sie neu. Die Regel liegt ohne
     JSX daneben, damit ein Prüfstand sie rechnen kann - eine Textsuche
     misst, dass etwas dasteht, nicht, dass es stimmt. */
  function slotAnklicken(u: string) {
    if (!aktiverTag) return;
    auswahlSetzen(aktiverTag.iso,
      zeitKlick(alleSlots, [...gewaehlteSlots], u, belegtHeute, vonHand));
  }

  /** Die mitgebuchten Fenster in die Auswahl nehmen - ein Handgriff. */
  function lueckeSchliessen() {
    if (!aktiverTag) return;
    auswahlSetzen(aktiverTag.iso, [...gewaehlteSlots, ...luecke]
      .sort((a, b) => zuMin(a) - zuMin(b)));
  }

  const dauer = ganz ? dauerTage(tage, ganz) : 0;

  return (
    /* EINE SPALTE, DIE ES NICHT GIBT, DARF KEINEN PLATZ BEKOMMEN.
       Die Tageleiste steht nur beim mehrtaegigen Tiefbau da. Stuende die
       dritte Spalte im Raster immer, blieben ohne sie 280 Bildpunkte BREITE
       leer - und genau die fehlen dem Kalender daneben. Die Klasse sagt es,
       statt dass das Stylesheet es raet. */
    <div className={'terminsteuerung' + (mehrtag ? ' mitleiste' : '')}>
      {/* ---------------------------------------------------- Der Monat */}
      <div className="kalenderseite">
        <div className="reihe kalenderkopf">
          <button type="button" className="knopf" aria-label="Monat zurück"
                  onClick={() => setMonat(monatVerschieben(monat, -1))}>‹</button>
          <b className="wachsen mitte">
            {MONATE[Number(monat.slice(5, 7)) - 1]} {monat.slice(0, 4)}
          </b>
          <button type="button" className="knopf" aria-label="Monat vor"
                  onClick={() => setMonat(monatVerschieben(monat, 1))}>›</button>
        </div>

        <table className="kalendergitter terminkalender">
          <thead>
            <tr>
              <th className="kw">KW</th>
              {WOCHENTAGE.map((w) => <th key={w}>{w}</th>)}
            </tr>
          </thead>
          <tbody>
            {zeilen(monat).map((zeile, i) => (
              <tr key={i}>
                <td className="kw">
                  {(() => {
                    const erster = zeile.find(Boolean);
                    if (!erster) return '';
                    const d = new Date(erster + 'T00:00:00');
                    return kalenderwoche(d);
                  })()}
                </td>
                {zeile.map((iso, j) => {
                  if (!iso) return <td key={j} className="termin-tag fremd" />;
                  const lage = lageVon(iso);
                  const eintrag = jeTag.get(iso);
                  const ist = gewaehlt.has(iso);
                  const klassen = ['termin-tag', lage.art];
                  if (ist) klassen.push('gewaehlt');
                  if (iso === aktiv) klassen.push('aufgeschlagen');
                  if (iso === heute) klassen.push('heute');
                  if (iso === kommtDazu) klassen.push('dazu');
                  /* ZU FRÜH FÜR DEN GEMEINDEVORLAUF: der Tag bleibt wählbar
                     und bekommt die Schraffur. Ein gesperrter Tag wäre falsch -
                     es gibt Fälle, in denen früher gebaut wird. */
                  const zuFrueh = p?.vorlauf?.gilt && p.vorlauf.fruehesterTag
                                  && iso < p.vorlauf.fruehesterTag
                                  && lage.waehlbar;
                  if (zuFrueh) klassen.push('zufrueh');
                  const titel = [
                    datum(iso), lage.grund,
                    zuFrueh ? `zu früh: die Gemeinde braucht bis `
                              + datum(p.vorlauf.fruehesterTag) : '',
                    ...(eintrag?.was ?? []),
                  ].filter(Boolean).join(', ');
                  return (
                    <td key={j} className={klassen.join(' ')} title={titel}
                        onClick={lage.waehlbar ? () => tagAnklicken(iso) : undefined}>
                      {/* DER INHALT STEHT IN EINEM KASTEN FESTER HÖHE.
                          Marvin, 06.09.2026: „damit die tage alle gleich groß
                          sind". An einer Tabellenzelle ist `height` eine
                          MINDESTHÖHE: ein Tag mit drei Zeilen macht seine
                          ganze WOCHE höher, und dann ist eine Zeile grösser
                          als die andere. */}
                      <div className="tagleib">
                        <span className="num">{Number(iso.slice(8, 10))}</span>
                        {!!eintrag?.was.length && (
                          <span className="cnt">
                            {eintrag.was.length === 1
                              ? '1 Termin' : `${eintrag.was.length} Termine`}
                          </span>
                        )}
                        {ist && (
                          <span className="cnt stark">
                            {tage.findIndex((t) => t.iso === iso) + 1}
                            {tage.length > 1 ? `/${tage.length}` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* DIE LEGENDE ERKLÄRT AUCH DIE SCHRAFFUR.
            ORIGINALTEXT MARVIN (03.09.2026)
              „straffierung in die legende unten bringen, die erinnerung
               unten kann dann weg"
            Sie stand nur im Tooltip - und einen Tooltip gibt es auf dem
            Telefon nicht. Ein Muster, das niemand deuten kann, sieht aus wie
            ein Fehler in der Anzeige. Das DATUM steht dabei: „zu früh"
            allein beantwortet die Frage nicht, ab wann es geht. */}
        <div className="reihe legende klein leise">
          <span><i className="punkt v" /> verfügbar gemeldet</span>
          <span><i className="punkt u" /> Urlaub / nicht verfügbar</span>
          <span><i className="punkt r" /> alle Zeiten belegt</span>
          <span><i className="punkt o" /> keine Meldung</span>
          {p?.vorlauf?.gilt && p.vorlauf.fruehesterTag && (
            <span className={p.zuFrueh?.zuFrueh ? 'warnwort' : ''}>
              <i className="punkt schraffiert" /> zu früh für den
              {' '}Gemeindevorlauf ({p.vorlauf.tage} Werktage): frühestens
              {' '}{datum(p.vorlauf.fruehesterTag)}
              {p.zuFrueh?.zuFrueh && ' - der gewählte Tag liegt davor'}
            </span>
          )}
        </div>

        {/* WOHER DIE BELEGUNG KOMMT - UND WANN SIE FEHLT.
            Ohne Firma weiss dieser Kalender nichts darüber, wo schon jemand
            fährt. **Ein Kalender, der leer aussieht, weil nichts geladen ist,
            ist von einem freien Monat nicht zu unterscheiden.** */}
        {!firma && (
          <div className="hinweis warnung klein" style={{ marginTop: 8 }}>
            Erst die Firma wählen. Bis dahin zeigt der Kalender nur die
            Wochenenden und die Feiertage - nicht, wo schon jemand fährt.
          </div>
        )}
        {firma && laedt && (
          <div className="klein leise" style={{ marginTop: 8 }}>Der Monat wird geholt.</div>
        )}
      </div>

      {/* ------------------------------------------- Die Zeiten des Tages */}
      <div className="tagesseite">
        {!aktiverTag ? (
          <div className="klein leise">Einen Tag im Kalender anklicken.</div>
        ) : (
          <>
            <b>{datum(aktiverTag.iso)}</b>
            {/* DIE GEWÄHLTE ZEIT IST DAS ERGEBNIS DIESES BILDSCHIRMS.
                Marvin, 07.09.2026: „außerdem ist oben deine markierte zeit
                sehr klein, das musst du was sichtbarer machen."
                Hier stand sie in `klein leise` - gemessen 12px in Grau,
                also in der Schrift, in der auch die Fussnoten stehen. Sie
                trägt jetzt die LESEGRÖSSE, den Akzent und die Dauer daneben.
                Eine dritte Schriftstufe gibt es in diesem Haus nicht: gross
                ist nur eine Überschrift, und das hier ist keine. */}
            <div className="gewaehltezeit">
              {aktiverTag.von} bis {aktiverTag.bis}
              <span className="klein leise">
                {', '}
                {(() => {
                  const min = zuMin(aktiverTag.bis) - zuMin(aktiverTag.von);
                  const h = Math.floor(min / 60);
                  const r = min % 60;
                  return (h ? `${h} h` : '') + (h && r ? ' ' : '')
                       + (r ? `${r} min` : '');
                })()}
                {tage.length > 1 && `, Tag ${tage.findIndex(
                  (t) => t.iso === aktiverTag.iso) + 1} von ${tage.length}`}
              </span>
            </div>

            {/* DIE DREI BLÖCKE DES BESTANDS. Sie tragen ihre Zeiten in der
                Aufschrift: „Vormittag" allein sagt nicht, ob das bis 11:30
                oder bis 12:00 geht - und genau das steht im Antrag an die
                Gemeinde. */}
            <div className="reihe blockreihe">
              {[['Ganzer Tag', fenster?.ganz], ['Vormittag', fenster?.vormittag],
                ['Nachmittag', fenster?.nachmittag]].map(([name, f]: any) => {
                const gilt = f && aktiverTag.von === f.von && aktiverTag.bis === f.bis;
                return f ? (
                  <button key={name} type="button"
                          className={'knopf klein' + (gilt ? ' haupt' : '')}
                          aria-pressed={gilt}
                          onClick={() => {
                            /* EIN BLOCKKNOPF SETZT DIE ZEIT UND NIMMT DIE
                               FREIE AUSWAHL ZURÜCK. Bliebe sie stehen, wäre
                               der nächste Klick ein „dazu" auf eine Auswahl,
                               die man gerade weggeworfen hat. */
                            const n = { ...auswahl };
                            delete n[aktiverTag.iso];
                            setAuswahl(n);
                            zeitSetzen(aktiverTag.iso, f.von, f.bis);
                          }}>
                    {name} ({f.von}-{f.bis})
                  </button>
                ) : null;
              })}
            </div>

            <div className="slotleiste">
              {alleSlots.map((u) => {
                const belegt = belegtHeute.has(u);
                const gew = gewaehlteSlots.has(u);
                const mit = !gew && luecke2.has(u);
                return (
                  <button key={u} type="button" disabled={belegt}
                          className={'slot' + (belegt ? ' belegt' : '')
                                     + (gew ? ' gewaehlt' : '')
                                     + (mit ? ' mit' : '')}
                          aria-pressed={gew}
                          title={belegt
                            ? 'belegt: ' + (jeTag.get(aktiverTag.iso)?.was
                                            .join(', ') ?? '')
                            : mit ? 'Nicht gewählt und trotzdem gebucht: '
                                    + 'ein Termin hat einen Anfang und ein Ende.'
                            : undefined}
                          onClick={() => slotAnklicken(u)}>
                    {u}
                  </button>
                );
              })}
            </div>

            {/* WAS MITGEBUCHT WIRD, OHNE GEWÄHLT ZU SEIN. Ein Bildschirm,
                der aus einer Auswahl mit Loch stillschweigend eine volle
                Spanne macht, bucht Zeit, die niemand angeklickt hat. */}
            {luecke.length > 0 && (
              <div className={'hinweis klein ' + (luekeBelegt ? 'warnung' : '')}
                   style={{ marginTop: 6 }}>
                {luekeBelegt ? (
                  <>
                    <b>Dazwischen liegt ein fremder Termin.</b> Gebucht würde
                    {' '}{aktiverTag.von} bis {aktiverTag.bis}, und darin steht
                    schon eine andere Buchung. Wähle eine Zeit, die zusammen
                    liegt.
                  </>
                ) : (
                  <>
                    <b>{luecke.length === 1 ? 'Eine halbe Stunde'
                        : `${luecke.length} halbe Stunden`} werden mitgebucht:
                    </b>{' '}{luecke.join(', ')}. Ein Termin hat einen Anfang
                    und ein Ende.{' '}
                    <button type="button" className="knopf klein"
                            onClick={lueckeSchliessen}>
                      Lücke mitnehmen
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="klein leise" style={{ marginTop: 6 }}>
              Jeder Klick wählt GENAU diese halbe Stunde - ein zweiter Klick
              nimmt sie wieder weg. Die 3 Knöpfe darüber setzen einen ganzen
              Block auf einmal.
            </div>
          </>
        )}
      </div>

      {/* ---------------------------------------------- Mehrtägiger Tiefbau */}
      {mehrtag && (
        <div className="tageleiste">
          <div className="kopf">Mehrtägiger Tiefbau</div>
          <div className="reihe">
            {tage.map((t) => (
              <span key={t.iso}
                    className={'tagchip' + (t.iso === aktiv ? ' aktiv' : '')}>
                <button type="button" className="marke"
                        onClick={() => setAktiv(t.iso)}>
                  {datum(t.iso).slice(0, 6)} {t.von}-{t.bis}
                </button>
                <button type="button" className="weg" title="Tag entfernen"
                        aria-label={`${datum(t.iso)} entfernen`}
                        onClick={() => tagEntfernen(t.iso)}>×</button>
              </span>
            ))}
            {ABKUERZUNGEN.map((n) => (
              <button key={n} type="button" className="knopf klein"
                      disabled={!tage.length}
                      onClick={() => abkuerzen(n)}>
                {dauerText(n)}
              </button>
            ))}
          </div>
          {tage.length > 1 ? (
            <div className="klein" style={{ marginTop: 4 }}>
              <b>{datum(tage[0].iso)} bis {datum(tage[tage.length - 1].iso)}</b>
              {', '}{dauerText(dauer)}
            </div>
          ) : (
            <div className="klein leise" style={{ marginTop: 4 }}>
              {kommtDazu
                ? `Für einen zweiten Tag einen weiteren anklicken - der `
                  + `nächste Werktag wäre der ${datum(kommtDazu)}.`
                : 'Erst einen Tag wählen.'}
            </div>
          )}
          {/* NICHTS VERSCHWINDET STILL. Wer drei Tage wählt und im Kalender den
              Montag danach sieht, soll den Grund lesen und nicht raten. */}
          {tage.length > 1 && (() => {
            const luecken: string[] = [];
            for (let i = 1; i < tage.length; i++) {
              let t = tage[i - 1].iso;
              for (let k = 0; k < 20; k++) {
                t = new Date(new Date(t + 'T00:00:00Z').getTime() + 86400000)
                  .toISOString().slice(0, 10);
                if (t === tage[i].iso) break;
                const name = feiertage.get(t);
                luecken.push(`${datum(t)} (${name
                  ?? WOCHENTAGE[(new Date(t + 'T00:00:00Z').getUTCDay() + 6) % 7]})`);
              }
            }
            return luecken.length ? (
              <div className="klein leise">Übersprungen: {luecken.join(', ')}</div>
            ) : null;
          })()}
        </div>
      )}
    </div>
  );
}

/** Die Wochenzeilen eines Monats, mit Leerfeldern vor dem Ersten. */
function zeilen(monat: string): (string | null)[][] {
  const felder: (string | null)[] = [];
  for (let i = 0; i < fuehrendeFelder(monat); i++) felder.push(null);
  felder.push(...tageImMonat(monat));
  while (felder.length % 7) felder.push(null);
  const aus: (string | null)[][] = [];
  for (let i = 0; i < felder.length; i += 7) aus.push(felder.slice(i, i + 7));
  return aus;
}
